<template>
  <my-giving-layout>
    <main v-if="isLoading">
      <div class="w-full h-full min-h-screen bg-[#F2EFED] relative">
        <Loading />
      </div>
    </main>
    <div class="bg-[#F6F4F0] w-full min-h-screen flex flex-col" v-else>
      <BackButton class="w-full px-[20px] mb-[16px]" :customBack="back" />
      <div
        class="flex-1 flex flex-col px-[20px] md:px-[24px] lg:px-[28px] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-[600] text-[#373531] pb-[64px]"
      >
        <div
          class="pt-[24px] text-[2rem] md:text-[3rem] lg:text-[3.25rem] leading-[2.5rem] md:leading-[3.5rem] lg:leading-[4rem] font-inter font-black text-[#2B2A28] tracking-[-0.96px]"
        >
          Manage My Giving
        </div>

        <div class="flex mt-[24px] bg-[#D5CFC8] rounded-[100px] p-[2px]">
          <button
            @click="setActiveTab('accounts')"
            class="flex-1 py-[8px] px-[8px] rounded-[100px] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-inter font-medium text-[#191817]"
            :class="activeTab === 'accounts' ? 'bg-white shadow-elevation-1' : ''"
          >
            Linked accounts
          </button>
          <button
            @click="setActiveTab('recurring')"
            class="flex-1 py-[8px] px-[8px] rounded-[100px] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-inter font-medium text-[#191817]"
            :class="activeTab === 'recurring' ? 'bg-white shadow-elevation-1' : ''"
          >
            Recurring giving
          </button>
        </div>

        <!-- pb clears the sticky FAB, which otherwise sits on top of the last
             section's action (the "Try again" in Couldn't be linked). -->
        <div v-if="activeTab === 'accounts'" class="pb-[96px]">
          <div class="flex flex-col gap-[12px] mt-[24px]">
            <div class="flex items-end gap-[16px]">
              <p
                class="flex-1 text-[0.875rem] leading-[1.25rem] font-inter font-semibold text-[#373531]"
              >
                Linked accounts
              </p>
              <img
                v-if="linkedBankAccounts.length > 0"
                src="/assets/icons/board/delete.svg"
                alt="Delete"
                class="w-[20px] h-[20px] shrink-0 cursor-pointer"
                :class="selectedAccountId ? 'opacity-100' : 'opacity-40'"
                @click="selectedAccountId && ((removeMode = 'linked'), (showRemoveModal = true))"
              />
            </div>
            <div v-if="linkedBankAccounts.length" class="flex flex-col gap-[12px]">
              <RadioCardSelection
                v-for="account in linkedBankAccounts"
                :key="account.eGiroMandateId"
                :bank-code="account.bankCode"
                :account-number="account.maskedBankAccNumber"
                :value="account.eGiroMandateId"
                :model-value="selectedAccountId"
                name="linkedAccounts"
                @update:model-value="selectedAccountId = $event"
              />
            </div>
            <div v-else class="flex gap-[12px]">
              <button
                @click="navigateTo('/my-giving/list-of-banks')"
                class="w-full py-[16px] px-[20px] rounded-[12px] border border-dashed border-[#A5A4A3] flex items-start cursor-pointer"
              >
                <img src="/assets/icons/plus.svg" />
                <div class="flex flex-col text-left ml-[8px]">
                  <div class="text-[1rem] font-[700] text-gray-800">Add bank account</div>
                  <div class="mt-[8px] text-[0.875rem] text-gray-500">
                    Give easily from your preferred bank account, without leaving the app.
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div v-if="bankAccountsPendingRemoval.length" class="flex flex-col mt-[24px] gap-[12px]">
            <p class="text-[0.875rem] leading-[1.25rem] font-inter font-semibold text-[#373531]">
              Pending to be removed
            </p>
            <div v-for="account in bankAccountsPendingRemoval" :key="account.eGiroMandateId">
              <div
                class="flex w-full h-full px-[20px] py-[16px] rounded-[10px] border-[1px] border-[#A5A4A3] items-center bg-[#E5E2DF]"
              >
                <div class="flex flex-col gap-[4px] flex-1">
                  <div class="flex flex-row items-center gap-[8px]">
                    <img
                      :src="iconFor(account.bankCode)"
                      :alt="account.bankName"
                      class="w-[24px] h-[24px] shrink-0"
                    />
                    <div
                      class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-inter font-medium text-[#21201E]"
                    >
                      {{ getBankShortName(account.bankCode) }}
                      {{ formatAccountNumber(account.maskedBankAccNumber) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="bankAccountsPendingApproval.length" class="flex flex-col mt-[24px] gap-[12px]">
            <p class="text-[0.875rem] leading-[1.25rem] font-inter font-semibold text-[#373531]">
              Pending to be linked
            </p>
            <div v-for="account in bankAccountsPendingApproval" :key="account.eGiroMandateId">
              <div
                class="flex w-full h-full px-[20px] py-[16px] rounded-[10px] border-[1px] border-[#A5A4A3] items-center"
              >
                <div class="flex flex-col gap-[4px] flex-1">
                  <div class="flex flex-row items-center gap-[8px]">
                    <img
                      :src="iconFor(account.bankCode)"
                      :alt="account.bankName"
                      class="w-[24px] h-[24px] shrink-0"
                    />
                    <div
                      class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-inter font-medium text-[#21201E]"
                    >
                      {{ getBankShortName(account.bankCode) }}
                      {{ formatAccountNumber(account.maskedBankAccNumber) }}
                    </div>
                  </div>
                </div>
                <!-- A mandate that never completes used to be a permanent dead
                     row with no self-service exit (ONE-8184). -->
                <button
                  type="button"
                  class="text-[0.875rem] leading-[1.25rem] font-inter font-semibold text-[#42362B] underline underline-offset-4 shrink-0"
                  :aria-label="`Cancel the pending link for ${getBankShortName(account.bankCode)} ${formatAccountNumber(account.maskedBankAccNumber)}`"
                  @click="askCancelPending(account)"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Mandates the bank rejected. Previously returned by the API and
               rendered by nothing, so the member saw the empty state as if they
               had never tried (ONE-8181). -->
          <div v-if="bankAccountsFailed.length" class="flex flex-col mt-[24px] gap-[12px]">
            <p class="text-[0.875rem] leading-[1.25rem] font-inter font-semibold text-[#373531]">
              Couldn't be linked
            </p>
            <div v-for="account in bankAccountsFailed" :key="account.eGiroMandateId">
              <div
                class="flex w-full h-full px-[20px] py-[16px] rounded-[10px] border-[1px] border-[#EC4E20] items-center gap-[12px]"
              >
                <div class="flex flex-col gap-[4px] flex-1">
                  <div class="flex flex-row items-center gap-[8px]">
                    <img
                      :src="iconFor(account.bankCode)"
                      :alt="account.bankName"
                      class="w-[24px] h-[24px] shrink-0"
                    />
                    <div
                      class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-inter font-medium text-[#21201E]"
                    >
                      {{ getBankShortName(account.bankCode) }}
                      {{ formatAccountNumber(account.maskedBankAccNumber) }}
                    </div>
                  </div>
                  <p class="text-[0.875rem] leading-[1.25rem] font-inter text-[#5F5A52]">
                    Your bank didn't complete the link. Nothing was set up, and you have not been
                    charged.
                  </p>
                </div>
                <button
                  type="button"
                  class="text-[0.875rem] leading-[1.25rem] font-inter font-semibold text-[#42362B] underline underline-offset-4 shrink-0"
                  :aria-label="`Try linking ${getBankShortName(account.bankCode)} again`"
                  @click="retryFailedLink(account)"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="showRemoveModal"
            class="fixed inset-0 bg-[#5D5D5D99] flex items-center justify-center z-50 px-[24px]"
            @click="showRemoveModal = false"
          >
            <div class="bg-white rounded-[12px] p-[24px] w-full" @click.stop>
              <!-- Name the account. The modal covers the radio selection, so
                   "Remove this account?" alone gave the member no way to check
                   which one they were about to remove (ONE-8189). -->
              <h3 class="text-[1.5rem] leading-[2rem] font-[900] text-[#2B2926]">
                {{ removeMode === 'pending' ? 'Cancel this request?' : 'Remove this account?' }}
              </h3>
              <p
                v-if="selectedAccountLabel"
                class="text-left text-[1rem] leading-[1.5rem] text-[#2B2926] font-[700] mt-[8px]"
              >
                {{ selectedAccountLabel }}
              </p>
              <template v-if="removeMode === 'pending'">
                <p
                  class="text-left text-[1rem] leading-[1.5rem] text-[#2B2926] font-[500] mt-[24px]"
                >
                  ⚠ This link was never completed, so nothing is set up and you have not been
                  charged. You can start it again at any time.
                </p>
              </template>
              <template v-else>
                <p
                  class="text-left text-[1rem] leading-[1.5rem] text-[#2B2926] font-[500] mt-[24px]"
                >
                  ⚠ All recurring giving arrangements using this account will also be removed.
                </p>
                <br />
                <p
                  class="text-left text-[1rem] leading-[1.5rem] text-[#2B2926] font-[500] mt-[24px]"
                >
                  ⚠ Scheduled transactions might still go through if removal or changes are made on
                  the day of scheduled transactions.
                </p>
              </template>
              <div class="flex gap-[16px] mt-[24px]">
                <button
                  @click="confirmRemoveAccount"
                  :disabled="removing"
                  class="flex-1 py-[12px] text-[1rem] leading-[1.5rem] font-[700] text-[#21201E]"
                  :class="{ 'opacity-50 cursor-not-allowed': removing }"
                >
                  {{ removing ? 'Removing...' : 'Confirm' }}
                </button>
                <button
                  @click="showRemoveModal = false"
                  :disabled="removing"
                  class="flex-1 py-[12px] px-[24px] text-[1rem] leading-[1.5rem] font-[700] text-[#21201E] rounded-[100px] border-solid border-[2px]"
                  :class="{ 'opacity-50 cursor-not-allowed': removing }"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="showErrorModal"
            class="fixed top-[86px] bg-[#EC4E20] text-[#fff] z-50 pl-[16px] pr-[8px] py-[8px] left-1/2 -translate-x-1/2 w-[90%] rounded-[8px] flex"
            @click="showErrorModal = false"
          >
            <div>
              {{
                removeErrorMessage ||
                'We were not able to reach the bank. Try again later. Alternatively, remove your recurring giving arrangements first.'
              }}
            </div>
            <div class="cursor-pointer basis-[10px]" @click="closeErrorModal">
              <CloseIcon fill="#fff" />
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'recurring'">
          <!-- Modal Overlay -->
          <Modal
            v-model="showConfirmationModal"
            :show-close="false"
            container-class="modal-custom-spacing"
          >
            <template #header>
              <div
                class="text-left text-[1.5rem] leading-[2rem] font-[900] text-[#2B2926] tracking-[-0.72px]"
              >
                Oops, you don't have a linked account yet!
              </div>
            </template>

            <div class="text-left text-[1rem] leading-[1.5rem] font-medium text-[#2B2926]">
              To set up recurring giving, add a bank account to HOGC ONE first!
            </div>

            <template #footer>
              <div class="flex gap-[16px] w-full">
                <button
                  class="flex-1 py-[12px] px-[24px] rounded-[100px] bg-transparent border-none text-[1rem] leading-[1.5rem] font-bold text-[#21201E] tracking-[-0.48px]"
                  @click="linkBankAccountLater"
                >
                  Maybe Later
                </button>
                <button
                  class="flex-1 py-[12px] px-[24px] rounded-[100px] bg-transparent border-solid border-[2px] border-[#42362B] text-[1rem] leading-[1.5rem] font-bold text-[#21201E] tracking-[-0.48px]"
                  @click="linkBankAccountNow"
                >
                  Add Now
                </button>
              </div>
            </template>
          </Modal>

          <!-- Giving per login: a login that has not confirmed the personal id can set
               up a new recurring gift, but does not see the ones already running. -->
          <DsAlert
            v-if="needsGivingVerification"
            variant="info"
            class="mt-[24px]"
            data-test="recurring-needs-verification"
          >
            Verify your personal ID to see your existing recurring giving. You can still set up a
            new one.
            <DsLink size="sm" underline="always" @click="goToVerify">Verify now</DsLink>
          </DsAlert>
          <RecurringGivingOption
            :key="Math.random()"
            :recurring-giving-list="recurringGivingList"
            :hide-buttons="showConfirmationModal"
          />
        </div>
      </div>
    </div>

    <!-- FAB - Floating Action Button -->
    <div
      v-if="!isLoading"
      class="sticky bottom-[24px] flex justify-end px-[20px] pb-[24px] z-10 pointer-events-none self-end"
    >
      <button
        @click="handleFabClick"
        class="w-[64px] h-[64px] rounded-full bg-[#42362B] flex items-center justify-center shadow-lg pointer-events-auto"
      >
        <img src="/assets/icons/my-giving/fab.svg" alt="Add" class="w-[40px] h-[40px]" />
      </button>
    </div>
  </my-giving-layout>
</template>

<script>
import BackButton from '@/components/common/BackButton.vue';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import RecurringGivingOption from '@/components/MyGiving/RecurringGivingPage/RecurringGivingOption.vue';
import RadioCardSelection from '@/components/MyGiving/Common/RadioCardSelection.vue';
import axios from 'axios';
import { mapGetters, mapState } from 'vuex';
import { openUrl } from '@/utils/browser-helper';
import Loading from '@/components/common/LoadingState';
import { BANK_ENUM } from '@/constants/MyGiving/bank-list';
import Modal from '@/components/common/Modal.vue';
import CloseIcon from '../../../public/assets/icons/board/CloseIcon';
import { Alert as DsAlert, Link as DsLink } from '@team-hogc/hogc-one-design-system';

export default {
  name: 'ManageGiving',
  components: {
    BackButton,
    MyGivingLayout,
    RecurringGivingOption,
    RadioCardSelection,
    Loading,
    Modal,
    CloseIcon,
    DsAlert,
    DsLink,
  },
  data() {
    return {
      selectedAccountId: null,
      isLoading: false,
      showConfirmationModal: false,
      removing: false,
      showErrorModal: false,
      removeErrorMessage: '',
      showRemoveModal: false,
      // 'linked' removes an active mandate; 'pending' cancels one the bank has
      // not completed. Same endpoint, different consequences, so the confirm
      // copy has to differ.
      removeMode: 'linked',
    };
  },
  async ionViewWillEnter() {
    this.isLoading = true;

    await Promise.all([
      this.$store.dispatch('myGiving/getLinkedBankAccounts'),
      this.$store.dispatch('myGiving/getRecurringGivingList'),
    ]);

    this.isLoading = false;
  },

  mounted() {
    if (!this.$route.hash) {
      this.$router.replace({ hash: '#accounts' });
    }
  },
  computed: {
    ...mapState('myGiving', ['linkedAccounts', 'recurringGivingList']),
    ...mapState('user', ['token']),
    ...mapGetters('myGiving', [
      'linkedBankAccounts',
      'bankAccountsPendingApproval',
      'bankAccountsPendingRemoval',
      'bankAccountsFailed',
      'needsGivingVerification',
    ]),

    activeTab() {
      const hash = this.$route.hash.substring(1);
      return ['accounts', 'recurring'].includes(hash) ? hash : 'accounts';
    },

    // Bank + last four for whichever account the confirm modal is about, so the
    // member can check it without dismissing the modal to look behind it.
    selectedAccountLabel() {
      if (!this.selectedAccountId) return '';
      const account = this.linkedAccounts.find(
        acc => acc.eGiroMandateId === this.selectedAccountId,
      );
      if (!account) return '';
      const lastFour = this.formatAccountNumber(account.maskedBankAccNumber);
      return [this.getBankShortName(account.bankCode), lastFour && `••${lastFour}`]
        .filter(Boolean)
        .join(' ');
    },
  },
  methods: {
    // The landing page shows the personal-id check for this login.
    goToVerify() {
      this.$router.push('/my-giving');
    },
    back() {
      this.$router.replace('/my-giving');
    },
    navigateTo(route) {
      this.$router.push(route);
    },
    async confirmRemoveAccount() {
      this.removing = true;
      let failed = false;

      try {
        // test error modal
        // TODO: remove test
        // throw new Error('No redirect URL received from server');

        const payload = {
          boDDARefNo: this.selectedAccountId,
        };

        const response = await axios.post(
          process.env.VUE_APP_GIVING_API_V2_URL + '/e-giro/authorize/cancellation',
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.user.token}`,
            },
          },
        );
        const redirectUrl = response.data;

        if (redirectUrl) {
          // Redirect to the bank's authentication page
          await openUrl(
            { url: redirectUrl },
            {
              title: 'Bank Auth',
              source: 'giving-manage',
              page: 'MyGiving',
            },
          );
        } else {
          throw new Error('No redirect URL received from server');
        }
      } catch (error) {
        console.error('Error Deleting Bank Account', error);

        // Show what the server actually said. The generic banner used to
        // replace it, which threw away the one instruction that unblocks the
        // member — e.g. "remove your recurring giving arrangements first"
        // (ONE-8188).
        this.removeErrorMessage =
          error.response?.data?.message ||
          error.message ||
          'We were not able to reach the bank. Try again later. Alternatively, remove your recurring giving arrangements first.';
        this.showErrorModal = true;
        failed = true;
      } finally {
        this.removing = false;
        this.showRemoveModal = false;
        // Keep the selection on failure so "try again" does not require
        // re-selecting the account.
        if (!failed) this.selectedAccountId = null;
      }
    },
    setActiveTab(tab) {
      this.$router.replace({ hash: `#${tab}` });
    },
    linkBankAccountLater() {
      this.showConfirmationModal = false;
    },
    iconFor(bankCode) {
      return BANK_ENUM[bankCode] && BANK_ENUM[bankCode].iconPath;
    },
    getBankShortName(bankCode) {
      return (BANK_ENUM[bankCode] && BANK_ENUM[bankCode].name) || 'Bank';
    },
    formatAccountNumber(maskedAccountNumber) {
      if (!maskedAccountNumber) return '';
      // Extract last 4 digits from the masked account number
      const lastFourDigits = maskedAccountNumber.replace(/\D/g, '').slice(-4);
      return `${lastFourDigits}`;
    },
    linkBankAccountNow() {
      this.showConfirmationModal = false;
      this.$router.push({ path: '/my-giving/list-of-banks', query: { fromrecurring: true } });
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.removeErrorMessage = '';
    },

    // Cancel a mandate the bank never completed (ONE-8184). Same cancellation
    // endpoint as removing a linked account — only the confirm copy differs,
    // because nothing is set up yet and no recurring giving can be attached.
    askCancelPending(account) {
      this.selectedAccountId = account.eGiroMandateId;
      this.removeMode = 'pending';
      this.showRemoveModal = true;
    },

    // Restart authorisation for a bank whose mandate failed (ONE-8181). Sends
    // the member back through the normal link flow for that same bank.
    retryFailedLink(account) {
      this.$router.push({ path: `/my-giving/link-bank/${account.bankCode}` });
    },

    handleFabClick() {
      if (this.activeTab === 'accounts') {
        this.navigateTo('/my-giving/list-of-banks');
      } else if (this.activeTab === 'recurring') {
        this.$router.push({ path: '/my-giving/giving-options', query: { fromrecurring: true } });
      }
    },
  },
};
</script>

<style scoped>
/* Modal custom spacing to match Figma design */
:deep(.modal-custom-spacing) {
  padding: 24px !important;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

:deep(.modal-custom-spacing > div:nth-child(1)) {
  /* Header div wrapper */
  margin-bottom: 0 !important;
  text-align: left !important;
}

:deep(.modal-custom-spacing > div:nth-child(1) h3) {
  /* Override header h3 default styles */
  font-size: 24px !important;
  line-height: 32px !important;
  font-weight: 900 !important;
  color: #2b2926 !important;
  letter-spacing: -0.72px !important;
  text-align: left !important;
  margin: 0 !important;
}

:deep(.modal-custom-spacing > div:nth-child(1) h3 > div) {
  /* Inner div in header */
  text-align: left !important;
}

:deep(.modal-custom-spacing > div:nth-child(2)) {
  /* Content div */
  margin-top: 0 !important;
  min-height: auto !important;
  max-height: none !important;
  text-align: left !important;
}

:deep(.modal-custom-spacing > div:nth-child(3)) {
  /* Footer div */
  margin-top: 0 !important;
  justify-content: center !important;
}
</style>
